import React from 'react';

import styled from 'styled-components';

import useConfigurations from '@hooks/Configurations/UseConfigurations';

import Money from '@components/Gui/Money';
import { getCommissionLabel, isMainStatementLine } from '@components/StatementRowsCommon/helpers';

import { useStatementContext } from '../StatementContext';
import StatementOriginalAmount from '../StatementOriginalAmount';
import StatementTaxesDetails from '../StatementTaxesDetails';

const TotalDetailsContainer = styled.div`
    dl.line-container {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin: 0;
        line-height: 1.25em;
        padding-top: 0.25rem;

        dt {
            text-transform: uppercase;
            font-weight: 300;
            font-size: ${(props) => props.theme.FontSizeXSm};
            color: ${(props) => props.theme.TextColorMuted};
            padding-right: 0.5rem;
        }

        dd {
            margin: 0;
            min-width: 6.8rem;
        }

        &.last {
            padding-bottom: 0.25rem;
        }
    }

    dl.tax-line-container {
        dt {
            padding-right: 0 !important;
            font-size: ${(props) => props.theme.FontSizeXSm};
        }

        dd {
            min-width: 6.8rem;
        }
    }
`;

/**
 * PayableTotalColumn
 *
 * Displays total details for payable (supplier) statements
 * Handles complex breakdown with commissions, advances, and taxes
 */
const PayableTotalColumn = ({ printable, transaction, isItemExpanded, hideTaxesForAgency }) => {
    // contacttype from statement context
    const { contactType } = useStatementContext();
    const configuration = useConfigurations();
    const statements = transaction.Statements || [];
    let mainLine = transaction.mainStatement || null;
    let commissionLine = transaction.commissionStatement || null;
    let clientAdvanceLine = transaction.clientAdvanceStatement || null;

    if (!mainLine && statements.length > 0) {
        mainLine = statements.find((item) => isMainStatementLine(item)) || statements[0];
    }
    if (!commissionLine && statements.length > 0) {
        commissionLine = statements.find((item) => item.isCommission) || null;
    }
    if (!clientAdvanceLine && statements.length > 0) {
        clientAdvanceLine = statements.find((item) => item.isClientAdvance) || null;
    }

    if (transaction.ModelStatements && transaction.ModelStatements.length > 0) {
        mainLine = transaction.ModelStatements[0];
    }
    const statementCurrency = transaction.Currency;
    const isDefaultCurrency = statementCurrency === configuration.defaultCurrency;

    const getTotalName = () => {
        switch (contactType.toLowerCase()) {
            case 'model':
                return 'Net for model';
            default:
                return 'Total To Invoice';
        }
    };

    const getMainLineName = () => {
        if (transaction.Family === 'JobFee') {
            switch (contactType.toLowerCase()) {
                case 'client':
                    return 'Fee';
                default:
                    return 'Gross';
            }
        } else {
            return 'Amount';
        }
    };

    const SingleLine = ({ taxed }) => {
        if (!isDefaultCurrency) {
            if (mainLine.ExchangeRate > 0) {
                return (
                    <>
                        <Money
                            redIfNegative={!printable}
                            amount={taxed ? mainLine.TaxedAmountDef : mainLine.AmountDef}
                            currency={configuration.defaultCurrency}
                        />
                        <StatementOriginalAmount
                            amount={taxed ? mainLine.TaxedAmount : mainLine.Amount}
                            currency={statementCurrency}
                            printable={printable}
                        />
                    </>
                );
            } else {
                return (
                    <Money
                        highlightCurrency={!printable}
                        redIfNegative={!printable}
                        amount={taxed ? mainLine.TaxedAmount : mainLine.Amount}
                        currency={statementCurrency}
                    />
                );
            }
        } else {
            return (
                <Money
                    redIfNegative={!printable}
                    amount={taxed ? mainLine.TaxedAmountDef : mainLine.AmountDef}
                    currency={configuration.defaultCurrency}
                />
            );
        }
    };

    const MainLine = () => {
        if (!isDefaultCurrency) {
            if (mainLine.ExchangeRate > 0) {
                return (
                    <dl className="line-container">
                        <dt>{getMainLineName()}</dt>
                        <dd>
                            <Money
                                redIfNegative={!printable}
                                amount={mainLine.AmountDef}
                                currency={configuration.defaultCurrency}
                            />
                            <StatementOriginalAmount
                                amount={mainLine.Amount}
                                currency={statementCurrency}
                                printable={printable}
                            />
                        </dd>
                    </dl>
                );
            } else {
                return (
                    <dl className="line-container">
                        <dt>{getMainLineName()}</dt>
                        <dd>
                            <Money
                                highlightCurrency={!printable}
                                redIfNegative={!printable}
                                amount={mainLine.Amount}
                                currency={statementCurrency}
                            />
                        </dd>
                    </dl>
                );
            }
        } else {
            return (
                <dl className="line-container">
                    <dt>{getMainLineName()}</dt>
                    <dd>
                        <Money
                            redIfNegative={!printable}
                            amount={mainLine.AmountDef}
                            currency={configuration.defaultCurrency}
                        />
                    </dd>
                </dl>
            );
        }
    };

    const CommissionLine = () => {
        if (!isDefaultCurrency) {
            if (commissionLine.ExchangeRate > 0) {
                return (
                    <dl className="line-container">
                        <dt>{getCommissionLabel({ statementType: contactType, variant: 'short' })}</dt>
                        <dd>
                            <Money
                                redIfNegative={!printable}
                                amount={commissionLine.AmountDef}
                                currency={configuration.defaultCurrency}
                            />
                            <StatementOriginalAmount
                                amount={commissionLine.Amount}
                                currency={statementCurrency}
                                printable={printable}
                            />
                        </dd>
                    </dl>
                );
            } else {
                return (
                    <dl className="line-container">
                        <dt>{getCommissionLabel({ statementType: contactType, variant: 'short' })}</dt>
                        <dd>
                            <Money
                                highlightCurrency={!printable}
                                redIfNegative={!printable}
                                amount={commissionLine.Amount}
                                currency={statementCurrency}
                            />
                        </dd>
                    </dl>
                );
            }
        } else {
            return (
                <dl className="line-container">
                    <dt>{getCommissionLabel({ statementType: contactType, variant: 'short' })}</dt>
                    <dd>
                        <Money
                            redIfNegative={!printable}
                            amount={commissionLine.AmountDef}
                            currency={configuration.defaultCurrency}
                        />
                    </dd>
                </dl>
            );
        }
    };

    const ClientAdvanceLine = () => {
        const clientAdvanceText = 'Client Advance / Payroll';
        if (!isDefaultCurrency) {
            if (clientAdvanceLine.ExchangeRate > 0) {
                return (
                    <dl className="line-container">
                        <dt>{clientAdvanceText}</dt>
                        <dd>
                            <Money
                                redIfNegative={!printable}
                                amount={clientAdvanceLine.AmountDef}
                                currency={configuration.defaultCurrency}
                            />
                            <StatementOriginalAmount
                                amount={clientAdvanceLine.Amount}
                                currency={statementCurrency}
                                printable={printable}
                            />
                        </dd>
                    </dl>
                );
            } else {
                return (
                    <dl className="line-container">
                        <dt>{clientAdvanceText}</dt>
                        <dd>
                            <Money
                                highlightCurrency={!printable}
                                redIfNegative={!printable}
                                amount={clientAdvanceLine.Amount}
                                currency={statementCurrency}
                            />
                        </dd>
                    </dl>
                );
            }
        } else {
            return (
                <dl className="line-container">
                    <dt>{clientAdvanceText}</dt>
                    <dd>
                        <Money
                            redIfNegative={!printable}
                            amount={clientAdvanceLine.AmountDef}
                            currency={configuration.defaultCurrency}
                        />
                    </dd>
                </dl>
            );
        }
    };

    const TotalLine = ({ showLabel }) => {
        if (!isDefaultCurrency) {
            return mainLine.ExchangeRate > 0 && commissionLine.ExchangeRate > 0 ? (
                showLabel ? (
                    <dl className="line-container">
                        <dt>{getTotalName()}</dt>
                        <dd>
                            <Money
                                highlightCurrency={!printable}
                                redIfNegative={!printable}
                                amount={mainLine.TaxedAmountDef + commissionLine.TaxedAmountDef}
                                currency={configuration.defaultCurrency}
                            />
                            <StatementOriginalAmount
                                amount={mainLine.TaxedAmount + commissionLine.TaxedAmount}
                                currency={statementCurrency}
                                printable={printable}
                            />
                        </dd>
                    </dl>
                ) : (
                    <p>
                        <Money
                            highlightCurrency={!printable}
                            redIfNegative={!printable}
                            amounts={[mainLine.TaxedAmountDef, commissionLine.TaxedAmountDef]}
                            currency={configuration.defaultCurrency}
                        />
                        <StatementOriginalAmount
                            amounts={[mainLine.TaxedAmount, commissionLine.TaxedAmount]}
                            currency={statementCurrency}
                            printable={printable}
                        />
                    </p>
                )
            ) : mainLine.ExchangeRate === 0 && commissionLine.ExchangeRate === 0 ? (
                showLabel ? (
                    <dl className="line-container">
                        <dt>{getTotalName()}</dt>
                        <dd>
                            <Money
                                highlightCurrency={!printable}
                                redIfNegative={!printable}
                                amount={mainLine.TaxedAmount + commissionLine.TaxedAmount}
                                currency={statementCurrency}
                            />
                        </dd>
                    </dl>
                ) : (
                    <p>
                        <Money
                            highlightCurrency={!printable}
                            redIfNegative={!printable}
                            amounts={[mainLine.TaxedAmount, commissionLine.TaxedAmount]}
                            currency={statementCurrency}
                        />
                    </p>
                )
            ) : isItemExpanded ? (
                <></>
            ) : (
                <>
                    <MainLine />
                    <CommissionLine />
                </>
            );
        } else {
            if (showLabel) {
                if (clientAdvanceLine) {
                    return (
                        <dl className="line-container last">
                            <dt>{getTotalName()}</dt>
                            <dd>
                                <Money
                                    highlightCurrency={!printable}
                                    redIfNegative={!printable}
                                    amounts={[
                                        mainLine.TaxedAmount,
                                        commissionLine.TaxedAmount,
                                        clientAdvanceLine.TaxedAmount,
                                    ]}
                                    currency={statementCurrency}
                                />
                            </dd>
                        </dl>
                    );
                } else {
                    return (
                        <dl className="line-container last">
                            <dt>{getTotalName()}</dt>
                            <dd>
                                <Money
                                    highlightCurrency={!printable}
                                    redIfNegative={!printable}
                                    amounts={[mainLine.TaxedAmount, commissionLine.TaxedAmount]}
                                    currency={statementCurrency}
                                />
                            </dd>
                        </dl>
                    );
                }
            } else {
                if (clientAdvanceLine) {
                    return (
                        <p>
                            <Money
                                highlightCurrency={!printable}
                                redIfNegative={!printable}
                                amounts={[
                                    mainLine.TaxedAmount,
                                    commissionLine.TaxedAmount,
                                    clientAdvanceLine.TaxedAmount,
                                ]}
                                currency={statementCurrency}
                            />
                        </p>
                    );
                } else {
                    return (
                        <p>
                            <Money
                                highlightCurrency={!printable}
                                redIfNegative={!printable}
                                amounts={[mainLine.TaxedAmount, commissionLine.TaxedAmount]}
                                currency={statementCurrency}
                            />
                        </p>
                    );
                }
            }
        }
    };

    if (contactType.toLowerCase() === 'agency') {
        if (!commissionLine) {
            return (
                <TotalDetailsContainer>
                    <SingleLine />
                    {!hideTaxesForAgency && (
                        <StatementTaxesDetails
                            blankIfNoTaxes
                            statement={mainLine}
                            statementCurrency={statementCurrency}
                        />
                    )}
                </TotalDetailsContainer>
            );
        } else {
            return (
                <TotalDetailsContainer>
                    <MainLine />
                    {!hideTaxesForAgency && (
                        <StatementTaxesDetails
                            blankIfNoTaxes
                            statement={mainLine}
                            statementCurrency={statementCurrency}
                        />
                    )}
                    <CommissionLine />
                    {clientAdvanceLine && <ClientAdvanceLine />}
                    {!hideTaxesForAgency && (
                        <StatementTaxesDetails statement={commissionLine} statementCurrency={statementCurrency} />
                    )}
                </TotalDetailsContainer>
            );
        }
    }

    if (!commissionLine) {
        if (isItemExpanded) {
            return (
                <TotalDetailsContainer>
                    <SingleLine />
                    <StatementTaxesDetails statement={mainLine} statementCurrency={statementCurrency} />
                    {mainLine.Amount !== mainLine.TaxedAmount && <SingleLine taxed />}
                </TotalDetailsContainer>
            );
        } else {
            return (
                <TotalDetailsContainer>
                    <p>
                        <SingleLine taxed />
                    </p>
                </TotalDetailsContainer>
            );
        }
    } else {
        if (isItemExpanded) {
            return (
                <TotalDetailsContainer>
                    <MainLine />
                    <StatementTaxesDetails statement={mainLine} statementCurrency={statementCurrency} />
                    <CommissionLine />
                    {clientAdvanceLine && <ClientAdvanceLine />}
                    <StatementTaxesDetails statement={commissionLine} statementCurrency={statementCurrency} />
                    <TotalLine showLabel />
                </TotalDetailsContainer>
            );
        } else {
            return (
                <TotalDetailsContainer>
                    <TotalLine />
                </TotalDetailsContainer>
            );
        }
    }
};

export default PayableTotalColumn;
